import { useQuery } from '@tanstack/react-query';
import { getHttpClient } from '@vnetwork/margarita-core/http';
import type { Envelope } from '../envelope';
import { normalizeId, sameId } from './org-workspaces';
import { findSharedWorkspace } from './shared-workspaces';
import { iamKeys } from './types';

const foundationApi = getHttpClient('foundationApi');

export interface SharedProjectGrant {
  role_id: string;
  role_title: string;
  via: 'user' | 'team';
  team_name?: string;
  /** The parent project the role was granted on, when this project is below it. */
  from_project?: string;
}

/** An active project the caller holds a role in, in any organization (GET /iam/me/shared-projects). */
export interface SharedProject {
  project_id: string;
  name: string;
  parent_id?: string | null;
  org_id: string;
  org_name: string;
  customer_id: string;
  grants: SharedProjectGrant[];
}

/** "Platform: Compute Viewer through team Dat-G" — what a person was given on one project, in words. */
export function describeSharedProject(p: SharedProject): string {
  const how = p.grants
    .map((g) => {
      const through = g.via === 'team' ? `through team ${g.team_name || '(unnamed)'}` : 'given to you';
      return `${g.role_title} ${through}${g.from_project ? `, from ${g.from_project}` : ''}`;
    })
    .join('; ');
  return how ? `${p.name}: ${how}` : p.name;
}

/** Projects of other organizations (and your own) that a role reaches you on; their names are not readable otherwise. */
export function useSharedProjects() {
  return useQuery({
    queryKey: iamKeys.mySharedProjects,
    staleTime: 5 * 60_000,
    retry: false,
    queryFn: async (): Promise<SharedProject[]> => {
      const res = await foundationApi.get<Envelope<{ projects?: SharedProject[] }>>('/iam/me/shared-projects');
      return res.data?.data?.projects ?? [];
    },
  });
}

/**
 * THE name of an organization, wherever a person reads whose workspace it is (the project switcher, the banner and notices, the
 * document title, the toasts, the create summary, the home-organization note): the organization's own name as the foundation lists
 * it on a project of it the person reaches (`org_name`), and only when no project names it, the compute or Kubernetes workspace's
 * own label (`fallback`, a tenant name that may differ from it). Never read `organizationName` of a workspace entry for display
 * on its own: call this.
 */
export function organizationNameOf(
  organizationId: string | number | null | undefined,
  projects: readonly Pick<SharedProject, 'customer_id' | 'org_name'>[],
  fallback?: string | null,
): string | undefined {
  const id = normalizeId(organizationId);
  const named = id ? projects.find((p) => sameId(p.customer_id, id) && normalizeId(p.org_name) !== '') : undefined;
  return normalizeId(named?.org_name) || normalizeId(fallback) || undefined;
}

/**
 * Whose machines a compute console opened through a share shows: the organization's name as the person knows it
 * from Teams (taken from a project of it they reach), else the compute workspace's own label.
 */
export function sharedOrgName(
  serviceId: string | undefined,
  workspaces: { metadata_id: string; organization_id: string; name: string }[],
  projects: Pick<SharedProject, 'customer_id' | 'org_name'>[],
): string | undefined {
  const w = findSharedWorkspace(workspaces, serviceId);
  if (!w) return undefined;
  return organizationNameOf(w.organization_id, projects, w.name);
}

/** The organization a compute workspace opened by service id belongs to when it is not the home one, by name; undefined for the home or an unknown workspace. */
export function foreignWorkspaceOrgName(
  serviceId: string | undefined,
  home: string | undefined,
  workspaces: { metadata_id: string; organization_id: string; name: string }[],
  projects: Pick<SharedProject, 'customer_id' | 'org_name'>[],
): string | undefined {
  const w = findSharedWorkspace(workspaces, serviceId);
  if (!w || !home || sameId(w.organization_id, home)) return undefined;
  return sharedOrgName(serviceId, workspaces, projects) || 'that organization';
}

/** The names of the projects of that organization the person reaches, for a banner: "Platform, Default" or "Platform and 2 more". */
export function sharedProjectNames(
  serviceId: string | undefined,
  workspaces: { metadata_id: string; organization_id: string }[],
  projects: Pick<SharedProject, 'customer_id' | 'name'>[],
  max = 3,
): string {
  const w = findSharedWorkspace(workspaces, serviceId);
  if (!w) return '';
  const names = projects.filter((p) => p.customer_id === w.organization_id).map((p) => p.name);
  if (names.length <= max) return names.join(', ');
  return `${names.slice(0, max).join(', ')} and ${names.length - max} more`;
}

/**
 * One entry of the top-bar project selector. `organizationId` is the organization the project belongs to ('' for an own
 * project while the home organization is unknown); `orgName` is set only when the project is in another organization.
 */
export interface SwitcherProject {
  id: string;
  name: string;
  organizationId: string;
  orgName?: string;
}

/** Own active projects, then the projects of other organizations the person reaches (marked by `orgName`). */
export function switcherProjects(
  own: { id: string; name: string }[],
  shared: Pick<SharedProject, 'project_id' | 'name' | 'org_name' | 'customer_id'>[],
  ownCustomerId: string | undefined,
): SwitcherProject[] {
  const seen = new Set(own.map((p) => p.id));
  const members = shared
    .filter((p) => !seen.has(p.project_id) && !!ownCustomerId && p.customer_id !== ownCustomerId)
    .map((p) => ({ id: p.project_id, name: p.name, organizationId: p.customer_id, orgName: p.org_name ?? '' }));
  return [...own.map((p) => ({ id: p.id, name: p.name, organizationId: ownCustomerId ?? '' })), ...members];
}

/**
 * The project a compute console opened through a share is about: the one the person chose if it belongs to that
 * organization, else the only one they reach there, else none.
 */
export function sharedConsoleProject(
  serviceId: string | undefined,
  workspaces: { metadata_id: string; organization_id: string }[],
  projects: Pick<SharedProject, 'project_id' | 'customer_id'>[],
  chosen: string,
): string {
  const w = findSharedWorkspace(workspaces, serviceId);
  if (!w) return '';
  const ids = projects.filter((p) => p.customer_id === w.organization_id).map((p) => p.project_id);
  if (chosen && ids.includes(chosen)) return chosen;
  return ids.length === 1 ? ids[0] : '';
}

/**
 * Names of every project this person can read, by id: their own organization's projects and the ones shared with them in
 * other organizations (the first name seen for an id wins, the own list first). ONE source for every project-name column.
 */
export function projectNameMap(
  own: readonly { id: string; name: string }[],
  shared: readonly { project_id: string; name: string }[],
): Map<string, string> {
  const names = new Map<string, string>();
  for (const p of own) if (!names.has(p.id)) names.set(p.id, p.name);
  for (const p of shared) if (!names.has(p.project_id)) names.set(p.project_id, p.name);
  return names;
}

/** A workspace reached through a role on the whole organization: nothing is listed by project or by machine. */
export function isWholeOrganization(counts: { projectIds?: readonly string[]; machineIds?: readonly string[] }): boolean {
  return !counts.projectIds?.length && !counts.machineIds?.length;
}

/** The role titles every one of these projects lists in its grants: what a role on the whole organization shows on each. */
function commonRoleTitles(projects: readonly SharedProject[]): string[] {
  if (projects.length === 0) return [];
  const [first, ...rest] = projects.map((p) => new Set(p.grants.map((g) => g.role_title)));
  return [...first].filter((title) => rest.every((titles) => titles.has(title)));
}

/**
 * The one line of a share of the whole organization (Design spec A9): `Whole organization · <role>`, instead of the same
 * grant once per project. The role is the one every project lists; with none in common the line names no role. The landing
 * card and the Shared with you page both say it this way (`isWholeOrganization` decides when).
 */
export function wholeOrganizationLine(projects: readonly Pick<SharedProject, 'grants'>[]): string {
  const roles = commonRoleTitles(projects as readonly SharedProject[]);
  return roles.length > 0 ? `Whole organization · ${roles.join(', ')}` : 'Whole organization';
}
